import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Hammer, AlertCircle, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BuildCapacityWidget
 * --------------------
 * Shows real-time scarcity pulled from /api/stats/build-capacity:
 *   - builds started this week
 *   - slots remaining before the next dispatch month
 * Always renders (API returns sensible fallback on DB outage) — never shows
 * a fake "out of stock" message.
 */
const BuildCapacityWidget = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/stats/build-capacity`);
        if (mounted) setStats(data);
      } catch (e) {
        // Widget is decorative — fail silent so the hero never breaks
        console.warn("build-capacity unavailable:", e?.message || e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (!stats) return null;

  const urgent = stats.slots_remaining <= 5;
  const low = stats.slots_remaining <= 2;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.9 }}
      className="mt-8 mx-auto max-w-md"
      data-testid="build-capacity-widget"
    >
      <div className="flex items-center justify-center gap-3 sm:gap-5 px-4 py-2.5 bg-[#0A0A0A]/70 backdrop-blur border border-white/10">
        {/* Pulse dot */}
        <span className="relative flex w-2 h-2 shrink-0">
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${urgent ? "bg-red-400" : "bg-green-400"}`} />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${urgent ? "bg-red-400" : "bg-green-400"}`} />
        </span>

        {/* Builds this week */}
        <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-[#F4F1DE]/80 whitespace-nowrap" data-testid="builds-this-week">
          <Hammer className="w-3.5 h-3.5 text-[#E07A5F]" />
          <span><strong className="text-[#F4F1DE]">{stats.builds_this_week}</strong> builds started this week</span>
        </div>

        <span className="text-white/15">·</span>

        {/* Slots remaining */}
        <div className="flex items-center gap-1.5 text-[11px] sm:text-xs whitespace-nowrap" data-testid="slots-remaining">
          {low ? (
            <AlertCircle className="w-3.5 h-3.5 text-red-400" />
          ) : (
            <Users className="w-3.5 h-3.5 text-[#F5B945]" />
          )}
          <span className={low ? "text-red-300" : "text-[#F4F1DE]/80"}>
            <strong className={low ? "text-red-200" : "text-[#F4F1DE]"}>
              {stats.slots_remaining}
            </strong>{" "}
            {stats.slots_remaining === 1 ? "slot" : "slots"} left before {stats.dispatch_month_short || stats.dispatch_month} dispatch
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default BuildCapacityWidget;
